import React, { useState, useEffect } from 'react';

const App = () => {
  const [scrollY, setScrollY] = useState(0);
  const [formData, setFormData] = useState({ name: '', email: '', message: '', inquiry: 'general' });
  const [formStatus, setFormStatus] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');
  const [newsletterDismissed, setNewsletterDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Show newsletter popup after 15 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!newsletterDismissed) {
        setShowNewsletter(true);
      }
    }, 15000);
    return () => clearTimeout(timer);
  }, [newsletterDismissed]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('sending');
    
    try {
      const response = await fetch('https://formspree.io/f/mojeeoad', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newsletterEmail,
          form_type: 'Newsletter Signup',
          _subject: '[PositiveHealth] New Newsletter Signup',
        }),
      });
      
      if (response.ok) {
        setNewsletterStatus('success');
        setTimeout(() => {
          setShowNewsletter(false);
          setNewsletterDismissed(true);
        }, 2000);
      } else {
        setNewsletterStatus('error');
      }
    } catch (error) {
      setNewsletterStatus('error');
    }
  };

  const closeNewsletter = () => {
    setShowNewsletter(false);
    setNewsletterDismissed(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    try {
      const response = await fetch('https://formspree.io/f/mojeeoad', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          inquiry_type: formData.inquiry,
          message: formData.message,
          _subject: `[PositiveHealth] ${formData.inquiry === 'research' ? 'Research Inquiry' : formData.inquiry === 'participate' ? 'Study Participation Interest' : formData.inquiry === 'media' ? 'Media Inquiry' : 'General Inquiry'} from ${formData.name}`,
        }),
      });
      
      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '', inquiry: 'general' });
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  // Professional SVG Icon Components
  const Icons = {
    activity: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <rect width="48" height="48" rx="12" fill="#F0F7F1"/>
        <path d="M10 24h6l4-8 8 16 4-8h6" stroke="#3D8B4E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    nutrition: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <rect width="48" height="48" rx="12" fill="#F0F7F1"/>
        <path d="M24 14c-2.5 0-4 1.5-4 4v2h8v-2c0-2.5-1.5-4-4-4z" fill="#3D8B4E"/>
        <ellipse cx="24" cy="28" rx="10" ry="6" stroke="#3D8B4E" strokeWidth="2.5"/>
        <path d="M14 28c0 4 4.5 8 10 8s10-4 10-8" stroke="#3D8B4E" strokeWidth="2.5"/>
      </svg>
    ),
    sleep: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <rect width="48" height="48" rx="12" fill="#F0F7F1"/>
        <path d="M32 24c0-5.5-4.5-10-10-10-1 0-2 .15-3 .4 2.5 1.5 4 4.3 4 7.6 0 4.4-3.6 8-8 8-.5 0-1-.05-1.5-.15C15 34.5 19 38 24 38c5.5 0 10-4.5 10-10z" fill="#3D8B4E"/>
      </svg>
    ),
    stress: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <rect width="48" height="48" rx="12" fill="#F0F7F1"/>
        <circle cx="24" cy="24" r="10" stroke="#3D8B4E" strokeWidth="2.5"/>
        <circle cx="24" cy="24" r="4" fill="#3D8B4E"/>
        <path d="M24 10v4M24 34v4M10 24h4M34 24h4" stroke="#3D8B4E" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    ),
    social: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <rect width="48" height="48" rx="12" fill="#F0F7F1"/>
        <circle cx="20" cy="18" r="4" stroke="#3D8B4E" strokeWidth="2.5"/>
        <circle cx="28" cy="18" r="4" stroke="#3D8B4E" strokeWidth="2.5"/>
        <path d="M12 36c0-5 4-8 12-8s12 3 12 8" stroke="#3D8B4E" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    ),
    habits: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <rect width="48" height="48" rx="12" fill="#F0F7F1"/>
        <circle cx="24" cy="24" r="10" stroke="#3D8B4E" strokeWidth="2.5"/>
        <path d="M18 24l4 4 8-8" stroke="#3D8B4E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    sparkle: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#F0F7F1"/>
        <path d="M28 12l3 10 10 3-10 3-3 10-3-10-10-3 10-3 3-10z" fill="#3D8B4E"/>
        <circle cx="40" cy="16" r="2" fill="#4EAAB3"/>
        <circle cx="16" cy="40" r="2" fill="#4EAAB3"/>
      </svg>
    ),
    bolt: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#EFF7F8"/>
        <path d="M30 12L18 30h10l-4 14 16-20H30l4-12z" fill="#4EAAB3"/>
      </svg>
    ),
    target: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#F0F7F1"/>
        <circle cx="28" cy="28" r="14" stroke="#3D8B4E" strokeWidth="2.5"/>
        <circle cx="28" cy="28" r="8" stroke="#3D8B4E" strokeWidth="2.5"/>
        <circle cx="28" cy="28" r="3" fill="#3D8B4E"/>
      </svg>
    ),
    tools: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#EFF7F8"/>
        <rect x="16" y="20" width="8" height="20" rx="2" fill="#4EAAB3"/>
        <rect x="28" y="16" width="8" height="24" rx="2" fill="#4EAAB3" opacity="0.7"/>
        <rect x="40" y="24" width="8" height="16" rx="2" fill="#4EAAB3" opacity="0.5"/>
        <path d="M14 42h36" stroke="#4EAAB3" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    ),
    strength: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#F0F7F1"/>
        <path d="M20 28h16M24 24v8M32 24v8" stroke="#3D8B4E" strokeWidth="3" strokeLinecap="round"/>
        <circle cx="18" cy="28" r="4" fill="#3D8B4E"/>
        <circle cx="38" cy="28" r="4" fill="#3D8B4E"/>
        <rect x="14" y="26" width="4" height="4" rx="1" fill="#3D8B4E"/>
        <rect x="38" y="26" width="4" height="4" rx="1" fill="#3D8B4E"/>
      </svg>
    ),
    globe: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#EFF7F8"/>
        <circle cx="28" cy="28" r="14" stroke="#4EAAB3" strokeWidth="2.5"/>
        <ellipse cx="28" cy="28" rx="6" ry="14" stroke="#4EAAB3" strokeWidth="2.5"/>
        <path d="M14 28h28" stroke="#4EAAB3" strokeWidth="2.5"/>
        <path d="M18 20h20M18 36h20" stroke="#4EAAB3" strokeWidth="1.5"/>
      </svg>
    ),
    research: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#F0F7F1"/>
        <rect x="16" y="14" width="24" height="28" rx="2" stroke="#3D8B4E" strokeWidth="2.5"/>
        <path d="M22 22h12M22 28h12M22 34h8" stroke="#3D8B4E" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="38" cy="38" r="6" fill="#F0F7F1" stroke="#3D8B4E" strokeWidth="2.5"/>
        <path d="M42 42l4 4" stroke="#3D8B4E" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    ),
    chart: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#EFF7F8"/>
        <rect x="14" y="30" width="6" height="12" rx="1" fill="#4EAAB3"/>
        <rect x="25" y="22" width="6" height="20" rx="1" fill="#4EAAB3"/>
        <rect x="36" y="14" width="6" height="28" rx="1" fill="#4EAAB3"/>
        <path d="M12 44h32" stroke="#4EAAB3" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    ),
    world: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#F0F7F1"/>
        <circle cx="28" cy="28" r="14" stroke="#3D8B4E" strokeWidth="2.5"/>
        <path d="M28 14v28M14 28h28" stroke="#3D8B4E" strokeWidth="2"/>
        <ellipse cx="28" cy="28" rx="7" ry="14" stroke="#3D8B4E" strokeWidth="2"/>
        <path d="M16 20c4 2 8 3 12 3s8-1 12-3M16 36c4-2 8-3 12-3s8 1 12 3" stroke="#3D8B4E" strokeWidth="1.5"/>
      </svg>
    ),
    gdpr: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#EBF4FF"/>
        <circle cx="28" cy="28" r="12" stroke="#2563EB" strokeWidth="2.5"/>
        <circle cx="28" cy="28" r="6" stroke="#2563EB" strokeWidth="2"/>
        <path d="M28 16v4M28 36v4M16 28h4M36 28h4" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"/>
        <path d="M20 20l3 3M33 33l3 3M36 20l-3 3M20 36l3-3" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="28" cy="28" r="2" fill="#2563EB"/>
      </svg>
    ),
    ethics: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#F0FDF4"/>
        <path d="M28 14l12 6v10c0 8-12 12-12 12s-12-4-12-12V20l12-6z" stroke="#16A34A" strokeWidth="2.5" fill="none"/>
        <path d="M22 28l4 4 8-8" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    security: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="16" fill="#FEF3C7"/>
        <rect x="18" y="24" width="20" height="16" rx="3" stroke="#D97706" strokeWidth="2.5"/>
        <path d="M22 24v-4a6 6 0 1 1 12 0v4" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="28" cy="32" r="2" fill="#D97706"/>
        <path d="M28 34v3" stroke="#D97706" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  };

  const pillars = [
    { iconKey: 'activity', title: 'Physical Activity' },
    { iconKey: 'nutrition', title: 'Nutrition' },
    { iconKey: 'sleep', title: 'Sleep' },
    { iconKey: 'stress', title: 'Stress Management' },
    { iconKey: 'social', title: 'Social Connection' },
    { iconKey: 'habits', title: 'Healthy Habits' }
  ];

  const aiFeatures = [
    { title: 'Appreciative Inquiry Coaching', desc: 'Discover your strengths and envision your best possible health future', iconKey: 'sparkle' },
    { title: 'Coaching in the Moment', desc: 'Brief check-ins for immediate support with lifestyle decisions', iconKey: 'bolt' },
    { title: 'ICF Core Competencies', desc: 'Built on International Coaching Federation standards', iconKey: 'target' },
    { title: 'Evidence-Based Tools', desc: '30+ micro-interventions for calming, energising, and flourishing', iconKey: 'tools' },
    { title: 'VIA Strengths Integration', desc: 'Personal character strengths assessment and application', iconKey: 'strength' },
    { title: '24/7 Availability', desc: 'Multi-language voice coaching whenever you need support', iconKey: 'globe' },
    { title: 'GDPR Compliant', desc: 'Full compliance with EU General Data Protection Regulation and Irish data protection laws', iconKey: 'gdpr' },
    { title: 'Ethics Approved', desc: 'Research Ethics Committee approved with ongoing ethical oversight', iconKey: 'ethics' },
    { title: 'Data Security', desc: 'End-to-end encryption, secure servers, and strict data minimisation principles', iconKey: 'security' }
  ];

  const researchFeatures = [
    { iconKey: 'research', title: 'Evidence-Based', desc: 'Built on validated psychological frameworks and lifestyle medicine research' },
    { iconKey: 'chart', title: 'Scientifically Validated', desc: 'Undergoing controlled trials to measure real-world health and wellbeing outcomes' },
    { iconKey: 'world', title: 'Policy Aligned', desc: 'Supporting global positive health and wellbeing initiatives' },
  ];

  // Logo Component
  const Logo = ({ darkBg = false }) => (
    <a href="#" style={{ textDecoration: 'none', display: 'inline-block' }}>
      <img 
        src={process.env.PUBLIC_URL + '/images/logo.png'}
        alt="PositiveHealth AI Health Coaching" 
        style={{ 
          height: 50, 
          width: 'auto',
          background: darkBg ? 'white' : 'transparent',
          padding: darkBg ? '8px 12px' : 0,
          borderRadius: darkBg ? 8 : 0,
        }} 
      />
    </a>
  );

  return (
    <div style={{
      fontFamily: "'Instrument Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      background: '#FAFAF8',
      color: '#1A1A1A',
      minHeight: '100vh',
      overflow: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&family=Fraunces:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');
        
        * { box-sizing: border-box; margin: 0; padding: 0; }
        
        html { scroll-behavior: smooth; }
        
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.05); opacity: 1; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-in { animation: fadeUp 0.8s ease-out forwards; }
        
        .section-title {
          font-family: 'Fraunces', Georgia, serif;
          font-size: clamp(1.75rem, 5vw, 3.5rem);
          font-weight: 600;
          line-height: 1.2;
          letter-spacing: -0.02em;
        }
        
        .section-label {
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #3D8B4E;
          margin-bottom: 12px;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #3D8B4E 0%, #2D7B3E 100%);
          color: white;
          padding: 16px 32px;
          border: none;
          border-radius: 100px;
          font-family: 'Instrument Sans', sans-serif;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(61, 139, 78, 0.3);
        }
        
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(61, 139, 78, 0.4);
        }
        
        .btn-secondary {
          background: transparent;
          color: #1A1A1A;
          padding: 16px 32px;
          border: 2px solid #1A1A1A;
          border-radius: 100px;
          font-family: 'Instrument Sans', sans-serif;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-block;
        }
        
        .btn-secondary:hover {
          background: #1A1A1A;
          color: white;
        }
        
        .nav-link {
          font-size: 14px;
          font-weight: 500;
          color: #4A4A4A;
          text-decoration: none;
          transition: color 0.2s ease;
          cursor: pointer;
        }
        
        .nav-link:hover { color: #3D8B4E; }
        
        .card {
          background: white;
          border-radius: 24px;
          padding: 32px;
          box-shadow: 0 4px 40px rgba(0,0,0,0.04);
          transition: all 0.3s ease;
        }
        
        .card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 50px rgba(0,0,0,0.08);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #3D8B4E 0%, #4EAAB3 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .footer-link {
          font-size: 14px;
          color: #888;
          text-decoration: none;
          transition: color 0.2s ease;
          display: block;
        }
        
        .footer-link:hover {
          color: #3D8B4E;
        }
        
        .form-input {
          width: 100%;
          padding: 14px 16px;
          font-size: 16px;
          border: 2px solid #E5E5E5;
          border-radius: 12px;
          outline: none;
          transition: border-color 0.2s ease;
          font-family: inherit;
          background: white;
        }
        
        .form-input:focus {
          border-color: #3D8B4E;
        }
        
        .form-input::placeholder {
          color: #999;
        }
        
        /* Responsive styles */
        .desktop-nav { display: flex; }
        .mobile-nav-toggle { display: none; }
        .mobile-nav { display: none; }
        
        @media (max-width: 968px) {
          .desktop-nav { display: none !important; }
          .mobile-nav-toggle { display: block !important; }
          .mobile-nav.open { display: flex !important; }
        }
        
        @media (max-width: 768px) {
          .card { padding: 24px; }
        }
      `}</style>

      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: scrollY > 50 ? 'rgba(250, 250, 248, 0.95)' : 'transparent',
        backdropFilter: scrollY > 50 ? 'blur(20px)' : 'none',
        transition: 'all 0.3s ease',
        zIndex: 1000,
      }}>
        <Logo />
        
        {/* Desktop Navigation */}
        <div className="desktop-nav" style={{ display: 'flex', gap: 36, alignItems: 'center' }}>
          <a className="nav-link" href="#about">About</a>
          <a className="nav-link" href="#research">Research</a>
          <a className="nav-link" href="#technology">Technology</a>
          <a className="nav-link" href="#contact">Contact</a>
          <a href="#contact" className="btn-primary" style={{ padding: '12px 24px', textDecoration: 'none' }}>Get Started</a>
        </div>
        
        {/* Mobile Menu Toggle */}
        <button 
          className="mobile-nav-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 8,
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2">
            {mobileMenuOpen ? (
              <path d="M6 6l12 12M6 18L18 6" strokeLinecap="round"/>
            ) : (
              <path d="M3 12h18M3 6h18M3 18h18" strokeLinecap="round"/>
            )}
          </svg>
        </button>
      </nav>
      
      {/* Mobile Navigation Menu */}
      <div 
        className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}
        style={{
          display: 'none',
          position: 'fixed',
          top: 70,
          left: 0,
          right: 0,
          background: 'rgba(250, 250, 248, 0.98)',
          backdropFilter: 'blur(20px)',
          flexDirection: 'column',
          padding: '24px',
          gap: 16,
          zIndex: 999,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <a className="nav-link" href="#about" onClick={() => setMobileMenuOpen(false)} style={{ fontSize: 18, padding: '8px 0' }}>About</a>
        <a className="nav-link" href="#research" onClick={() => setMobileMenuOpen(false)} style={{ fontSize: 18, padding: '8px 0' }}>Research</a>
        <a className="nav-link" href="#technology" onClick={() => setMobileMenuOpen(false)} style={{ fontSize: 18, padding: '8px 0' }}>Technology</a>
        <a className="nav-link" href="#contact" onClick={() => setMobileMenuOpen(false)} style={{ fontSize: 18, padding: '8px 0' }}>Contact</a>
        <a href="#contact" className="btn-primary" onClick={() => setMobileMenuOpen(false)} style={{ textDecoration: 'none', textAlign: 'center', marginTop: 8 }}>Get Started</a>
      </div>

      {/* Hero Section */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: '120px 24px 80px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Background Elements */}
        <div style={{
          position: 'absolute',
          top: '5%',
          right: '-5%',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(61, 139, 78, 0.08) 0%, transparent 70%)',
          animation: 'pulse 8s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          left: '-10%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(78, 170, 179, 0.06) 0%, transparent 70%)',
          animation: 'pulse 6s ease-in-out infinite 2s',
        }} />
        
        <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 48, alignItems: 'center' }}>
            <div className="animate-in">
              <div className="section-label">AI-Enhanced Positive Health Coaching</div>
              <h1 className="section-title" style={{ marginBottom: 24 }}>
                Transform Your Wellbeing Through{' '}
                <span className="gradient-text">Positive Health Coaching</span>
              </h1>
              <p style={{ fontSize: 18, lineHeight: 1.7, color: '#555', marginBottom: 32, maxWidth: 540 }}>
                A groundbreaking AI coach combining Appreciative Inquiry with Lifestyle Medicine 
                to help you build on your strengths and flourish — not just survive.
              </p>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <a href="#about" className="btn-primary" style={{ textDecoration: 'none' }}>Learn More</a>
                <a href="#research" className="btn-secondary">Our Research</a>
              </div>
            </div>
            
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
              {/* Phone Mockup */}
              <div style={{
                background: 'linear-gradient(145deg, #2A2A2A 0%, #1A1A1A 100%)',
                borderRadius: 40,
                padding: 12,
                boxShadow: '0 40px 80px rgba(0,0,0,0.15), 0 20px 40px rgba(0,0,0,0.1)',
                animation: 'float 6s ease-in-out infinite',
                maxWidth: 320,
                width: '100%',
              }}>
              <div style={{
                background: 'linear-gradient(180deg, #FDFCFA 0%, #F5F4F2 100%)',
                borderRadius: 32,
                padding: 24,
                minHeight: 500,
              }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                  <div style={{ width: 80, height: 6, background: '#E5E5E5', borderRadius: 3 }} />
                </div>
                <div style={{ 
                  background: 'linear-gradient(135deg, #3D8B4E 0%, #4EAAB3 100%)', 
                  borderRadius: 20, 
                  padding: 20, 
                  color: 'white', 
                  marginBottom: 16 
                }}>
                  <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Your Coach</div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>
                    "What strength do you most want to bring to your wellbeing journey today?"
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ background: 'white', borderRadius: 16, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Today's Focus</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>Dream Phase: Best Possible Health</div>
                  </div>
                  <div style={{ background: 'white', borderRadius: 16, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Your Top Strength</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>💜 Perseverance</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ background: '#F0F7F1', borderRadius: 16, padding: 12, textAlign: 'center' }}>
                      <div style={{ fontSize: 24, marginBottom: 4 }}>🏃</div>
                      <div style={{ fontSize: 12, color: '#3D8B4E', fontWeight: 500 }}>Activity</div>
                    </div>
                    <div style={{ background: '#EFF7F8', borderRadius: 16, padding: 12, textAlign: 'center' }}>
                      <div style={{ fontSize: 24, marginBottom: 4 }}>🧘</div>
                      <div style={{ fontSize: 12, color: '#4EAAB3', fontWeight: 500 }}>Mindfulness</div>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section style={{ padding: '60px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 24 }}>
          {[
            { value: '6', label: 'Pillars of Positive Health' },
            { value: '30+', label: 'Evidence-Based Micro-Interventions' },
            { value: '5-D', label: 'Appreciative Inquiry Cycle' },
            { value: '24/7', label: 'AI Coach Availability' },
          ].map((stat, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(32px, 8vw, 48px)', fontWeight: 600, color: '#3D8B4E', marginBottom: 8 }}>
                {stat.value}
              </div>
              <div style={{ fontSize: 14, color: '#666' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section id="about" style={{ padding: '80px 24px', background: '#FAFAF8' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 48, alignItems: 'center' }}>
            <div>
              <div className="section-label">The Opportunity</div>
              <h2 className="section-title" style={{ marginBottom: 24 }}>
                Your Best Possible Health Starts With Your Strengths
              </h2>
              <p style={{ fontSize: 17, lineHeight: 1.8, color: '#555', marginBottom: 24 }}>
                Positive Health goes beyond the absence of illness — it's about flourishing across every 
                dimension of your wellbeing. Research shows that building on your strengths and cultivating 
                positive lifestyle habits leads to lasting, meaningful change.
              </p>
              <p style={{ fontSize: 17, lineHeight: 1.8, color: '#555', marginBottom: 32 }}>
                Our AI coach combines the science of positive psychology with lifestyle medicine — helping 
                you discover your strengths, envision your healthiest future, and create sustainable change 
                across all six pillars of positive health.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 12 }}>
                {pillars.map((pillar, i) => (
                  <div key={i} style={{ textAlign: 'center', padding: 16, background: 'white', borderRadius: 16 }}>
                    <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}>{Icons[pillar.iconKey]}</div>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#333' }}>{pillar.title}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ 
              background: 'linear-gradient(145deg, #F0F7F1 0%, #EFF7F8 100%)', 
              borderRadius: 32, 
              padding: 'clamp(24px, 5vw, 40px)',
              position: 'relative',
            }}>
              <div className="section-label">Our Approach</div>
              <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(22px, 4vw, 28px)', fontWeight: 600, marginBottom: 24 }}>
                The 5-D Appreciative Inquiry Cycle
              </h3>
              <p style={{ fontSize: 16, lineHeight: 1.7, color: '#555', marginBottom: 24 }}>
                Rather than focusing on what's wrong, we discover what gives life. Through guided 
                coaching, you'll envision your best possible health and design pathways to achieve it.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { phase: 'Define', desc: 'Clarify your focus and intentions' },
                  { phase: 'Discover', desc: 'Explore your strengths and peak experiences' },
                  { phase: 'Dream', desc: 'Envision your best possible health' },
                  { phase: 'Design', desc: 'Create your action plan' },
                  { phase: 'Destiny', desc: 'Sustain and celebrate your progress' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3D8B4E 0%, #4EAAB3 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}>
                      {i + 1}
                    </div>
                    <div>
                      <span style={{ fontSize: 15, fontWeight: 600 }}>{item.phase}</span>
                      <span style={{ fontSize: 14, color: '#666', marginLeft: 8 }}>{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Section */}
      <section id="research" style={{ padding: '80px 24px', background: 'linear-gradient(180deg, #FAFAF8 0%, #F5F4F2 100%)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <div className="section-label">Research-Backed</div>
          <h2 className="section-title" style={{ marginBottom: 24 }}>
            Rigorous Research, Real Results
          </h2>
          <p style={{ fontSize: 18, lineHeight: 1.8, color: '#555', marginBottom: 40, maxWidth: 700, margin: '0 auto 40px' }}>
            The Positive Health Coach is being validated through rigorous research, 
            ensuring our AI coaching delivers measurable improvements in health outcomes 
            and psychological wellbeing.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 24, marginBottom: 40 }}>
            {researchFeatures.map((item, i) => (
              <div key={i} className="card" style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>{Icons[item.iconKey]}</div>
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{item.title}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: '#666' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section id="technology" style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="section-label">The Technology</div>
            <h2 className="section-title" style={{ marginBottom: 16 }}>
              AI Coaching Grounded in Science
            </h2>
            <p style={{ fontSize: 17, color: '#666', maxWidth: 700, margin: '0 auto' }}>
              Combining cutting-edge AI with evidence-based positive psychology and ICF coaching standards.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {aiFeatures.slice(0, 6).map((feature, i) => (
              <div key={i} className="card" style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>{Icons[feature.iconKey]}</div>
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{feature.title}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: '#666' }}>{feature.desc}</p>
              </div>
            ))}
          </div>
          
          {/* Ethics & Compliance Subsection */}
          <div style={{ marginTop: 64 }}>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <div className="section-label">Ethics & Compliance</div>
              <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(24px, 5vw, 32px)', fontWeight: 600, marginBottom: 16 }}>
                Your Data, Protected
              </h3>
              <p style={{ fontSize: 17, color: '#666', maxWidth: 600, margin: '0 auto' }}>
                Built with the highest standards of ethical oversight and data protection from the ground up.
              </p>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
              {aiFeatures.slice(6).map((feature, i) => (
                <div key={i} className="card" style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>{Icons[feature.iconKey]}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{feature.title}</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: '#666' }}>{feature.desc}</p>
                </div>
              ))}
            </div>
            
            <div style={{ 
              marginTop: 32, 
              padding: 'clamp(20px, 4vw, 32px)', 
              background: 'linear-gradient(145deg, #F8FAFC 0%, #F1F5F9 100%)', 
              borderRadius: 20,
              textAlign: 'center'
            }}>
              <p style={{ fontSize: 15, color: '#555', lineHeight: 1.8, maxWidth: 800, margin: '0 auto' }}>
                <strong>Our commitment to your privacy:</strong> All personal health data is processed in accordance with 
                GDPR, the Irish Data Protection Act 2018, and health data regulations. Participation in research is 
                voluntary with full informed consent. You maintain complete control over your data with the right to 
                access, rectify, or delete your information at any time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" style={{ padding: '80px 24px', background: 'linear-gradient(180deg, #FAFAF8 0%, #F5F4F2 100%)' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div className="section-label">Get In Touch</div>
            <h2 className="section-title" style={{ marginBottom: 16 }}>
              Contact Us
            </h2>
            <p style={{ fontSize: 17, color: '#666' }}>
              Have questions about the Positive Health Coach or interested in participating in our research? We'd love to hear from you.
            </p>
          </div>
          
          <div className="card" style={{ padding: 'clamp(24px, 5vw, 40px)' }}>
            <form onSubmit={handleFormSubmit}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#333' }}>
                  Name *
                </label>
                <input
                  type="text"
                  required
                  className="form-input"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#333' }}>
                  Email *
                </label>
                <input
                  type="email"
                  required
                  className="form-input"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#333' }}>
                  Inquiry Type
                </label>
                <select
                  className="form-input"
                  value={formData.inquiry}
                  onChange={(e) => setFormData({...formData, inquiry: e.target.value})}
                  style={{ cursor: 'pointer' }}
                >
                  <option value="general">General Inquiry</option>
                  <option value="participate">Study Participation</option>
                  <option value="research">Research Collaboration</option>
                  <option value="media">Media & Press</option>
                </select>
              </div>
              
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#333' }}>
                  Message *
                </label>
                <textarea
                  required
                  rows={5}
                  className="form-input"
                  placeholder="How can we help you?"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  style={{ resize: 'vertical', minHeight: 120 }}
                />
              </div>
              
              <button
                type="submit"
                disabled={formStatus === 'sending'}
                className="btn-primary"
                style={{ 
                  width: '100%', 
                  padding: '16px 32px',
                  opacity: formStatus === 'sending' ? 0.7 : 1,
                  cursor: formStatus === 'sending' ? 'not-allowed' : 'pointer',
                }}
              >
                {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
              
              {formStatus === 'success' && (
                <div style={{ 
                  marginTop: 16, 
                  padding: 16, 
                  background: '#F0F7F1', 
                  borderRadius: 12, 
                  color: '#3D8B4E',
                  textAlign: 'center',
                  fontWeight: 500,
                }}>
                  Thank you! Your message has been sent successfully.
                </div>
              )}
              
              {formStatus === 'error' && (
                <div style={{ 
                  marginTop: 16, 
                  padding: 16, 
                  background: '#FEF2F2', 
                  borderRadius: 12, 
                  color: '#DC2626',
                  textAlign: 'center',
                  fontWeight: 500,
                }}>
                  Sorry, there was an error. Please try again or email us directly.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '80px 24px',
        background: 'linear-gradient(135deg, #3D8B4E 0%, #4EAAB3 100%)',
        color: 'white',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(28px, 6vw, 42px)', fontWeight: 600, marginBottom: 20 }}>
            Ready to Transform Your Wellbeing?
          </h2>
          <p style={{ fontSize: 'clamp(16px, 3vw, 18px)', lineHeight: 1.7, opacity: 0.9, marginBottom: 32 }}>
            Discover how positive psychology and AI coaching can help you build sustainable 
            healthy habits and flourish — not just survive.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="#contact" style={{
              background: 'white',
              color: '#3D8B4E',
              padding: '18px 36px',
              border: 'none',
              borderRadius: 100,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'none',
            }}>
              Get Started
            </a>
            <a href="#about" style={{
              background: 'transparent',
              color: 'white',
              padding: '18px 36px',
              border: '2px solid white',
              borderRadius: 100,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'none',
            }}>
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '60px 24px', background: '#1A1A1A', color: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 40, marginBottom: 40 }}>
            <div style={{ gridColumn: 'span 1' }}>
              <div style={{ marginBottom: 16 }}>
                <Logo darkBg={true} />
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: '#888', maxWidth: 300 }}>
                AI-Enhanced Appreciative Inquiry Positive Health Coaching.
              </p>
            </div>
            <div>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Navigation</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <a href="#about" className="footer-link">About</a>
                <a href="#research" className="footer-link">Research</a>
                <a href="#technology" className="footer-link">Technology</a>
                <a href="#contact" className="footer-link">Contact</a>
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Resources</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <a href="https://coachingfederation.org" target="_blank" rel="noopener noreferrer" className="footer-link">International Coaching Federation</a>
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #333', paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ fontSize: 13, color: '#666' }}>
              © 2026 PositiveHealth AI Coaching. All rights reserved.
            </div>
            <div style={{ fontSize: 13, color: '#666' }}>
              Dublin, Ireland
            </div>
          </div>
        </div>
      </footer>

      {/* Newsletter Popup */}
      {showNewsletter && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: 24,
        }}>
          <div style={{
            background: 'white',
            borderRadius: 24,
            padding: 'clamp(24px, 5vw, 40px)',
            maxWidth: 480,
            width: '100%',
            position: 'relative',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            {/* Close button */}
            <button
              onClick={closeNewsletter}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 8,
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                <path d="M6 6l12 12M6 18L18 6" strokeLinecap="round"/>
              </svg>
            </button>

            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ 
                width: 64, 
                height: 64, 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #3D8B4E 0%, #4EAAB3 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 600, marginBottom: 8 }}>
                Stay Updated
              </h3>
              <p style={{ fontSize: 15, color: '#666', lineHeight: 1.6 }}>
                Be the first to know about our research progress, app launch, and opportunities to participate in our study.
              </p>
            </div>

            {newsletterStatus === 'success' ? (
              <div style={{ 
                padding: 20, 
                background: '#F0F7F1', 
                borderRadius: 12, 
                textAlign: 'center',
                color: '#3D8B4E',
                fontWeight: 500,
              }}>
                Thank you for signing up! We'll be in touch.
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit}>
                <input
                  type="email"
                  required
                  placeholder="Enter your email address"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="form-input"
                  style={{ marginBottom: 16 }}
                />
                <button
                  type="submit"
                  disabled={newsletterStatus === 'sending'}
                  className="btn-primary"
                  style={{ 
                    width: '100%', 
                    marginBottom: 16,
                    opacity: newsletterStatus === 'sending' ? 0.7 : 1,
                    cursor: newsletterStatus === 'sending' ? 'not-allowed' : 'pointer',
                  }}
                >
                  {newsletterStatus === 'sending' ? 'Signing up...' : 'Sign Up for Updates'}
                </button>
                {newsletterStatus === 'error' && (
                  <div style={{ 
                    marginBottom: 16,
                    padding: 12, 
                    background: '#FEF2F2', 
                    borderRadius: 8, 
                    color: '#DC2626',
                    textAlign: 'center',
                    fontSize: 14,
                  }}>
                    Sorry, there was an error. Please try again.
                  </div>
                )}
              </form>
            )}

            {/* GDPR Disclaimer */}
            <div style={{ 
              fontSize: 12, 
              color: '#888', 
              lineHeight: 1.6,
              textAlign: 'center',
              borderTop: '1px solid #E5E5E5',
              paddingTop: 16,
            }}>
              <p style={{ marginBottom: 8 }}>
                <strong>Your privacy matters to us.</strong>
              </p>
              <p>
                By signing up, you consent to receive occasional emails about our research and app updates. 
                Your email address will be stored securely and used solely for this purpose. We will never 
                share your data with third parties. You can unsubscribe at any time by contacting us. 
                In accordance with GDPR, you have the right to access, rectify, or delete your personal data.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
